import { useEffect, useRef } from 'react';
import { Star } from 'lucide-react';
import { gsap } from '../lib/gsapConfig';

const testimonials = [
  {
    name: 'Georgia K.',
    location: 'Denver, CO',
    move: 'Long-distance move',
    date: 'May 2026',
    initials: 'GK',
    avatarBg: 'bg-blue-500',
    source: 'google',
    quote: 'Got four quotes within an hour. Picked the one with the best reviews and the crew showed up right on time. Would absolutely use this again.',
  },
  {
    name: 'James R.',
    location: 'Austin, TX',
    move: 'Local apartment move',
    date: 'April 2026',
    initials: 'JR',
    avatarBg: 'bg-green-600',
    source: 'trustpilot',
    quote: 'Did not expect it to be this simple. Filled out the form, compared companies, booked the one with weekend availability. Saved me hours of phone calls.',
  },
  {
    name: 'Michelle C.',
    location: 'Atlanta, GA',
    move: 'Office relocation',
    date: 'March 2026',
    initials: 'MC',
    avatarBg: 'bg-purple-600',
    source: 'google',
    quote: 'Relocated our 12-person office over a weekend. Easy to filter for commercial movers and everything was set up before Monday morning.',
  },
];

// Cards are rendered back-to-front — last in DOM is on top
const CARD_CLASSES = [
  // Back card
  "[grid-area:stack] hover:-translate-y-10 before:absolute before:w-full before:h-full before:rounded-2xl before:content-[''] before:bg-white/65 before:left-0 before:top-0 before:transition-opacity before:duration-700 hover:before:opacity-0 grayscale hover:grayscale-0 transition-all duration-700 cursor-pointer",
  // Middle card
  "[grid-area:stack] translate-x-14 translate-y-10 hover:-translate-y-1 before:absolute before:w-full before:h-full before:rounded-2xl before:content-[''] before:bg-white/65 before:left-0 before:top-0 before:transition-opacity before:duration-700 hover:before:opacity-0 grayscale hover:grayscale-0 transition-all duration-700 cursor-pointer",
  // Front card
  "[grid-area:stack] translate-x-28 translate-y-20 hover:translate-y-10 transition-all duration-700 cursor-pointer",
];

const GoogleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M21.6 12.24c0-.68-.06-1.32-.17-1.94H12v3.69h5.32a4.55 4.55 0 01-1.97 2.99v2.47h3.19C20.45 17.86 21.6 15.26 21.6 12.24z" fill="#4285F4"/>
    <path d="M12 22c2.67 0 4.91-.88 6.54-2.39l-3.19-2.47c-.89.6-2.02.95-3.35.95-2.58 0-4.76-1.74-5.54-4.08H3.16v2.55A9.99 9.99 0 0012 22z" fill="#34A853"/>
    <path d="M6.46 14.01A6.02 6.02 0 016.15 12c0-.7.12-1.38.31-2.01V7.44H3.16A9.99 9.99 0 002 12c0 1.58.38 3.08.9 4.32l3.56-2.31z" fill="#FBBC05"/>
    <path d="M12 5.92c1.45 0 2.76.5 3.79 1.48l2.83-2.83C16.91 2.99 14.67 2 12 2A9.99 9.99 0 003.16 7.44l3.3 2.55C7.24 7.67 9.42 5.92 12 5.92z" fill="#EA4335"/>
  </svg>
);

const TrustpilotIcon = () => (
  <svg width="14" height="14" viewBox="0 0 32 32" fill="none">
    <rect width="32" height="32" rx="4" fill="#00b67a"/>
    <path d="M16 6.5l2.34 7.2h7.56l-6.12 4.44 2.34 7.2L16 20.9l-6.12 4.44 2.34-7.2L6.1 13.7h7.56L16 6.5z" fill="white"/>
  </svg>
);

function DisplayCard({ testimonial, className }) {
  const Icon = testimonial.source === 'google' ? GoogleIcon : TrustpilotIcon;
  const platformName = testimonial.source === 'google' ? 'Google' : 'Trustpilot';

  return (
    <div
      className={[
        'relative flex h-52 w-80 sm:w-[22rem] -skew-y-[8deg] select-none flex-col justify-between',
        'rounded-2xl border-2 border-slate-200 bg-slate-50/80 backdrop-blur-sm px-5 py-4',
        'after:absolute after:-right-1 after:top-[-5%] after:h-[110%] after:w-60',
        'after:bg-gradient-to-l after:from-white after:to-transparent after:content-[\'\'] after:pointer-events-none',
        'hover:border-primary/30 hover:bg-white',
        className,
      ].join(' ')}
    >
      {/* Stars + platform */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="h-3.5 w-3.5 fill-[#EB9813] text-[#EB9813]" />
          ))}
        </div>
        <div className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2 py-1 shadow-sm">
          <Icon />
          <span className="text-[10px] font-semibold text-slate-500">{platformName}</span>
        </div>
      </div>

      {/* Quote */}
      <p className="text-sm leading-6 text-slate-700 line-clamp-3">
        "{testimonial.quote}"
      </p>

      {/* Author */}
      <div className="flex items-center gap-2.5">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${testimonial.avatarBg} text-xs font-black text-white`}>
          {testimonial.initials}
        </div>
        <div>
          <p className="text-sm font-bold text-slate-900 leading-tight">{testimonial.name}</p>
          <p className="text-[11px] text-slate-400">{testimonial.location} · {testimonial.date}</p>
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.testimonial-copy', {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 80%',
        },
        y: 30,
        opacity: 0,
        duration: 0.65,
        stagger: 0.08,
        ease: 'power3.out',
      });
      gsap.from('.testimonial-stack', {
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 75%',
        },
        x: 40,
        opacity: 0,
        duration: 0.8,
        delay: 0.2,
        ease: 'power3.out',
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="bg-white py-16 sm:py-20 overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">

          {/* Left — copy */}
          <div className="max-w-lg">
            <span className="testimonial-copy inline-flex rounded-full bg-primary/10 px-4 py-2 text-sm font-bold text-primary">
              Customer reviews
            </span>
            <h2 className="testimonial-copy mt-4 text-3xl font-extrabold tracking-normal text-slate-950 sm:text-4xl">
              Real people, real moves
            </h2>
            <p className="testimonial-copy mt-4 text-lg leading-8 text-slate-500">
              Thousands of customers have used MyMoveAdvisor to find and book trusted movers. Hover each card to read their story.
            </p>

            <div className="testimonial-copy mt-8 grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <p className="text-2xl font-extrabold text-slate-950">4.9 / 5</p>
                <p className="mt-1 text-sm text-slate-500">Average rating across all reviews</p>
              </div>
              <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <p className="text-2xl font-extrabold text-slate-950">1,200+</p>
                <p className="mt-1 text-sm text-slate-500">Verified Google and Trustpilot reviews</p>
              </div>
            </div>
          </div>

          {/* Right — stacked cards */}
          <div className="testimonial-stack flex items-center justify-center py-16 pl-4 lg:justify-end lg:pr-16">
            <div className="grid [grid-template-areas:'stack'] place-items-center">
              {testimonials.map((t, i) => (
                <DisplayCard
                  key={t.name}
                  testimonial={t}
                  className={CARD_CLASSES[i]}
                />
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
